import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchTestimonials } from "@/lib/api";
import { TIDS } from "@/constants/testIds";

function StarRow({ n = 5 }) {
  return (
    <div className="flex gap-1 text-[#FFC629]">
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} size={16} fill="#FFC629" strokeWidth={0} />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetchTestimonials()
      .then(setItems)
      .catch(() => {});
  }, []);

  return (
    <div className="relative">
      <section className="pt-36 pb-16 grain relative overflow-hidden">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10 relative z-10">
          <p className="text-xs uppercase tracking-[0.35em] text-[#1D2AE8]/80 font-semibold">
            Testimonials · From founders, not accounts
          </p>
          <h1
            className="font-display leading-[0.92] mt-6"
            style={{ fontSize: "clamp(3rem, 10vw, 8rem)" }}
          >
            One hundred and six
            <br />
            reasons to <em>trust us.</em>
          </h1>
          <div className="mt-8 flex items-center gap-6">
            <div className="flex items-center gap-2">
              <StarRow />
              <span className="font-semibold">4.98 / 5</span>
            </div>
            <span className="text-sm text-[#1D2AE8]/70">
              Average founder rating across 106 engagements
            </span>
          </div>
        </div>
      </section>

      <section className="py-16 pb-32">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-10">
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
            {items.map((t, i) => (
              <motion.article
                key={t.id || i}
                data-testid={TIDS.testimonial(i)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className={`mb-6 break-inside-avoid rounded-3xl p-7 ${
                  i % 4 === 1
                    ? "glass-dark text-white"
                    : "glass-light"
                }`}
              >
                <div className="flex items-center justify-between">
                  <StarRow n={t.rating || 5} />
                  <Quote
                    size={22}
                    className={
                      i % 4 === 1 ? "text-[#FFC629]" : "text-[#0B0B0B]/40"
                    }
                  />
                </div>
                <p
                  className={`font-display text-[1.35rem] leading-snug mt-5 ${
                    i % 4 === 1 ? "text-white" : "text-[#0B0B0B]"
                  }`}
                >
                  "{t.quote}"
                </p>
                <div
                  className={`mt-6 pt-4 border-t ${
                    i % 4 === 1
                      ? "border-white/10 text-white/80"
                      : "border-[#0B0B0B]/10 text-[#1D2AE8]/80"
                  } text-sm`}
                >
                  <p className="font-semibold">{t.author}</p>
                  <p className="text-xs uppercase tracking-[0.2em] mt-1 opacity-70">
                    {t.role} · {t.brand}
                  </p>
                  {t.location && (
                    <p className="text-xs mt-1 opacity-60">{t.location}</p>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0B0B0B] text-[#FFF6E0]">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10 text-center">
          <h2 className="font-display text-5xl sm:text-6xl leading-[0.95]">
            Ready to add your name
            <br />
            to this <em className="text-[#FFC629]">list</em>?
          </h2>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 bg-[#FFC629] text-[#0B0B0B] font-semibold rounded-full px-6 py-3 hover:bg-[#FFE0EC]"
          >
            Let's talk <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
